import React, { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { CardBody, Button } from 'reactstrap';
import bg from '../../assets/images/id-card-bg.jpg';
import logo from '../../assets/images/app-logo.png';
import { fetchImageAsBlob } from '../../api/image';

function selectParentsForIdCard(parents) {
  const valid = (parents || []).filter((p) => p?.parent);

  if (valid.length <= 4) return valid;

  const relationOrder = [];
  const groups = {};

  for (const p of valid) {
    if (!groups[p.relation]) {
      groups[p.relation] = [];
      relationOrder.push(p.relation);
    }
    groups[p.relation].push(p);
  }

  const selected = [];
  for (const relation of relationOrder) {
    const group = groups[relation];
    const pick = group[Math.floor(Math.random() * group.length)];
    selected.push(pick);
    if (selected.length === 4) break;
  }
  return selected;
}

const getParentGridStyle = (count, responsive) => {
  const gap = responsive ? '3cqw' : '10px';

  if (count <= 1) {
    return {
      display: 'grid',
      gridTemplateColumns: '1fr',
      justifyItems: 'center',
      gap,
      width: '100%'
    };
  }

  return {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap,
    width: '100%'
  };
};

const ChildIdCardTemplate = ({ imgUrl, childInfo, item }) => {
  const [parentImages, setParentImages] = useState([]);

  const addressOptions = [
    {
      value:
        'White House, Mahatma Gandhi Rd, near Lakshminarayan Temple, Kandivali, Bhagat Colony, Kandivali West, Mumbai, Maharashtra 400067',
      label: 'MG'
    },
    {
      value:
        'Shop 02, Off Link Road, Extension, Mahavir Nagar, Kandivali West, Mumbai, Maharashtra 400067',
      label: 'MN'
    },
    {
      value:
        'PLOT NO 154, RSC 15, Shri Ganeshkrupa Society, Samabhav Society, Gorai 2, Borivali West, Mumbai, Maharashtra 400091',
      label: 'GB'
    }
  ];

  const getBranchCode = (batchName) => {
    if (!batchName) return 'MG';

    const name = batchName.toUpperCase();

    if (name.includes('MN')) return 'MN';
    if (name.includes('GB')) return 'GB';
    if (name.includes('MG')) return 'MG';

    return 'MG';
  };

  const getAddressFromBatch = (batchName) => {
    const branch = getBranchCode(batchName);

    const found = addressOptions.find((a) => a.label === branch);

    return found?.value || addressOptions[0]?.value;
  };

  useEffect(() => {
    const fetchParentImages = async () => {
      let defaultImageUrl = '';

      try {
        defaultImageUrl = await fetchImageAsBlob(
          process.env.REACT_APP_PROFILE_IMG
        );
      } catch (err) {
        console.log(err);
      }

      const selectedParents = selectParentsForIdCard(childInfo?.parents);
      const images = [];

      for (const parentEntry of selectedParents) {
        const key = parentEntry?.parent?.profile_picture?.documentKey;
        let url = defaultImageUrl;

        if (key) {
          try {
            url = await fetchImageAsBlob(key);
          } catch (err) {
            console.log(err);
          }
        }

        images.push({
          url,
          relation: parentEntry?.relation,
          name: parentEntry?.parent?.name
        });
      }

      setParentImages(images);
    };

    fetchParentImages();
  }, [childInfo]);

  const frontExportRef = useRef();
  const backExportRef = useRef();

  const waitForImages = async (ref) => {
    const images = ref.current.querySelectorAll('img');

    await Promise.all(
      [...images].map((img) => {
        if (img.complete) return Promise.resolve();

        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );
  };

  const downloadCard = async (ref, fileName) => {
    try {
      await document.fonts.ready;

      await waitForImages(ref);

      const canvas = await html2canvas(ref.current, {
        scale: 4,
        useCORS: true
      });

      const image = canvas.toDataURL('image/jpeg', 1.0);

      const link = document.createElement('a');

      link.href = image;
      link.download = fileName;

      link.click();
    } catch (err) {
      console.log(err);
    }
  };

  const CardFront = ({ responsive = false }) => (
    <div
      className="position-relative rounded overflow-hidden"
      style={{
        width: responsive ? '100%' : '300px',
        maxWidth: responsive ? '300px' : 'unset',
        aspectRatio: '3/5',
        height: responsive ? 'auto' : '500px',
        flexShrink: 0,
        containerType: responsive ? 'inline-size' : 'unset'
      }}
    >
      <img
        src={bg}
        alt="background"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />

      <div
        className="position-absolute bg-white rounded-lg d-flex flex-column align-items-center"
        style={{
          inset: responsive ? '8.3%' : '25px',
          padding: responsive ? '5%' : '16px',
          justifyContent: 'space-between',
          border: '1px solid #000'
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <img
            src={logo}
            alt="logo"
            style={{ width: responsive ? '20cqw' : '60px' }}
          />

          <h6
            className="m-0"
            style={{
              fontSize: responsive ? '6.6cqw' : '20px',
              fontWeight: 'bold'
            }}
          >
            RAISING RIGHT
          </h6>
        </div>

        {imgUrl && (
          <div
            style={{
              width: responsive ? '66.6%' : '200px',
              height: responsive ? '66.6cqw' : '200px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <img
              src={imgUrl}
              alt={childInfo?.name}
              style={{
                borderRadius: '12px',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </div>
        )}

        <h6
          className="text-center m-0"
          style={{
            fontSize: responsive ? '6cqw' : '18px',
            fontWeight: 'bold'
          }}
        >
          {childInfo?.name}
        </h6>

        <div
          style={{
            width: '100%',
            height: '1px',
            background: '#000'
          }}
        />

        <p
          className="m-0 text-center"
          style={{
            fontSize: responsive ? '4.3cqw' : '13px',
            color: '#000'
          }}
        >
          {item?.name}
        </p>

        <div className="w-100 text-center">
          {childInfo?.parents?.find((p) => p.relation === 'Mother') && (
            <p
              className="m-0 fw-semibold"
              style={{
                fontSize: responsive ? '3.6cqw' : '11px',
                color: '#000'
              }}
            >
              MOTHER:{' '}
              {childInfo?.parents?.find((p) => p.relation === 'Mother')?.parent
                ?.phone_number || 'N/A'}
            </p>
          )}

          {childInfo?.parents?.find((p) => p.relation === 'Father') && (
            <p
              className="m-0 fw-semibold"
              style={{
                fontSize: responsive ? '3.6cqw' : '11px',
                color: '#000'
              }}
            >
              FATHER:{' '}
              {childInfo?.parents?.find((p) => p.relation === 'Father')?.parent
                ?.phone_number || 'N/A'}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const CardBack = ({ responsive = false }) => (
    <div
      className="position-relative rounded overflow-hidden"
      style={{
        width: responsive ? '100%' : '300px',
        maxWidth: responsive ? '300px' : 'unset',
        aspectRatio: '3/5',
        height: responsive ? 'auto' : '500px',
        flexShrink: 0,
        containerType: responsive ? 'inline-size' : 'unset'
      }}
    >
      <img
        src={bg}
        alt="background"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />

      <div
        className="position-absolute bg-white rounded-lg d-flex flex-column align-items-center"
        style={{
          inset: responsive ? '8.3%' : '25px',
          padding: responsive ? '5%' : '16px',
          justifyContent: 'space-between',
          border: '1px solid #000'
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <img
            src={logo}
            alt="logo"
            style={{ width: responsive ? '20cqw' : '60px' }}
          />

          <h6
            className="m-0"
            style={{
              fontSize: responsive ? '6.6cqw' : '20px',
              fontWeight: 'bold'
            }}
          >
            RAISING RIGHT
          </h6>
        </div>

        <div style={getParentGridStyle(parentImages.length, responsive)}>
          {parentImages.map((parent, index) => (
            <img
              key={`${parent.relation}-${parent.name}-${index}`}
              width={responsive ? '100%' : '100px'}
              height={responsive ? 'auto' : '100px'}
              src={parent.url}
              alt={parent.name || parent.relation}
              style={{
                objectFit: 'fill',
                borderRadius: responsive ? '2.6cqw' : '8px',
                aspectRatio: '1/1',
                maxWidth:
                  parentImages.length === 1
                    ? responsive
                      ? '50%'
                      : '100px'
                    : '100%'
              }}
            />
          ))}
        </div>

        <div
          style={{
            width: '100%',
            height: '1px',
            background: '#000'
          }}
        />

        <div>
          <p
            className="text-center m-0"
            style={{
              fontSize: responsive ? '3.6cqw' : '11px',
              color: '#000',
              fontWeight: 'bolder'
            }}
          >
            IF FOUND LOST, PLEASE RETURN TO THE BELOW ADDRESS
          </p>

          <p
            className="text-center m-0"
            style={{
              fontSize: responsive ? '3.6cqw' : '11px',
              color: '#000'
            }}
          >
            {getAddressFromBatch(item?.name)}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <CardBody className="d-flex flex-column align-items-center">
      {/* Responsive ID card component which is shown in the UI */}
      <div className="d-flex gap-4 flex-wrap justify-content-center w-100">
        <CardFront responsive />
        <CardBack responsive />
      </div>

      {/* Hidden Front Card */}
      <div
        style={{
          position: 'fixed',
          left: '-99999px',
          top: 0,
          zIndex: -1
        }}
      >
        <div
          ref={frontExportRef}
          style={{
            width: '320px',
            padding: '10px',
            background: '#fff'
          }}
        >
          <CardFront />
        </div>
      </div>

      {/* Hidden Back Card */}
      <div
        style={{
          position: 'fixed',
          left: '-99999px',
          top: 0,
          zIndex: -1
        }}
      >
        <div
          ref={backExportRef}
          style={{
            width: '320px',
            padding: '10px',
            background: '#fff'
          }}
        >
          <CardBack />
        </div>
      </div>

      <div
        className="d-flex flex-wrap justify-content-center w-100 mt-3"
        style={{
          gap: '20px'
        }}
      >
        <Button
          color="primary"
          className="px-4 py-2 fw-bold"
          onClick={() =>
            downloadCard(frontExportRef, `${childInfo?.name}-front-id-card.jpg`)
          }
        >
          Download Front
        </Button>

        <Button
          color="secondary"
          className="px-4 py-2 fw-bold"
          onClick={() =>
            downloadCard(backExportRef, `${childInfo?.name}-back-id-card.jpg`)
          }
        >
          Download Back
        </Button>
      </div>
    </CardBody>
  );
};

export default ChildIdCardTemplate;
